import * as React from 'react';
import {
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {useSearch} from './search';
import {
  ItemSelectionContextValue,
  ItemSelectionOptions,
  SearchAssetInformationDto,
} from '../models';
import {isPresent} from '../utils/utils';

const initialValue: ItemSelectionContextValue = {
  selectedItems: [],
  selectedItem: undefined,
  selectedItemIds: new Set<string>(),
  showSidebar: false,
  changeSelectedItems: () => {},
  isSelected: () => false,
  closeSidebar: () => {},
};

export const ItemSelectionContext =
  React.createContext<ItemSelectionContextValue>(initialValue);

export const useItemSelection = () => useContext(ItemSelectionContext);

export const ItemSelectionProvider: React.FC<PropsWithChildren> = ({
  children,
}) => {
  const [selectedItemIndices, setSelectedItemIndices] = useState<Set<number>>(
    new Set(),
  );
  const [selectedItem, setSelectedItem] = useState<
    SearchAssetInformationDto | undefined
  >();
  const [showSidebar, setShowSidebar] = useState<boolean>(false);
  const [lastSelectedItem, setLastSelectedItem] = useState<
    SearchAssetInformationDto | undefined
  >();
  const {result, firstPage, searching} = useSearch();

  const selectedItems = useMemo(() => {
    return Array.from(selectedItemIndices)
      .map(index => result.content[index])
      .filter(item => isPresent<SearchAssetInformationDto>(item));
  }, [selectedItemIndices, result]);

  // Set für O(1) Lookup statt O(n) Array.includes()
  const selectedItemIds = useMemo(() => {
    return new Set(selectedItems.map(item => item.id));
  }, [selectedItems]);

  // Schnelle Prüfung ob ein Item ausgewählt ist
  const isSelected = useCallback(
    (item: SearchAssetInformationDto): boolean => selectedItemIds.has(item.id),
    [selectedItemIds],
  );

  useEffect(() => {
    if (!searching && firstPage) {
      setLastSelectedItem(undefined);
      setSelectedItemIndices(new Set());
      setSelectedItem(undefined);
      setShowSidebar(false);
    }
  }, [searching, firstPage]);

  useEffect(() => {
    if (
      selectedItem &&
      !result.content.find(item => item?.id === selectedItem.id)
    ) {
      setSelectedItem(undefined);
    }
  }, [result, selectedItem]);

  const changeSelectedItems = useCallback(
    (
      item: SearchAssetInformationDto,
      itemSelctionOptions: ItemSelectionOptions,
    ) => {
      const itemIndex = result.content.findIndex(
        other => item.id === other?.id,
      );
      if (itemIndex === -1) return;

      const products = result ? result['content'] : [];
      let newItems: number[] = [itemIndex];

      // Wenn Shift gedrückt wird, wird die Auswahl zwischen dem letzten und dem aktuellen Element erweitert
      if (lastSelectedItem && itemSelctionOptions.shift) {
        const startIndex = products.findIndex(
          product => product?.id === lastSelectedItem.id,
        );
        const endIndex = products.findIndex(product => product?.id === item.id);

        if (startIndex !== -1 && endIndex !== -1) {
          const [from, to] =
            startIndex < endIndex
              ? [startIndex, endIndex]
              : [endIndex, startIndex];

          newItems = products.map((_, index) => index).slice(from, to + 1);
        }
        // Bei Shift: Selektion wird erweitert, lastSelectedItem aktualisiert, Sidebar wird geschlossen
        setSelectedItemIndices(new Set([...selectedItemIndices, ...newItems]));
        setLastSelectedItem(item);
        setShowSidebar(false);
        return;
      }

      // Ctrl-Klick: Multi-Selektion
      if (itemSelctionOptions.crtl) {
        // Sidebar wird bei Ctrl-Klick immer geschlossen (Multi-Selektion-Modus)
        setShowSidebar(false);

        // Wenn das Element bereits ausgewählt ist, wird es abgewählt
        if (selectedItems.includes(item)) {
          newItems = Array.from(selectedItemIndices).filter(
            index => index !== itemIndex,
          );
          setSelectedItemIndices(new Set(newItems));
          if (newItems.length === 0) {
            setLastSelectedItem(undefined);
            setSelectedItem(undefined);
          }
        } else {
          // Element wird zur Auswahl hinzugefügt
          setSelectedItemIndices(new Set([...selectedItemIndices, itemIndex]));
          setLastSelectedItem(item);
        }
        return;
      }

      // Drag Event: Keine Sidebar öffnen
      if (itemSelctionOptions.dragEvent) {
        setSelectedItemIndices(new Set([itemIndex]));
        setSelectedItem(item);
        setLastSelectedItem(item);
        return;
      }

      const isSameItemClicked =
        selectedItems.length === 1 && selectedItems[0]?.id === item.id;

      if (isSameItemClicked) {
        if (showSidebar) {
          // Panel ist offen -> deselektieren und Panel schließen
          setSelectedItemIndices(new Set());
          setSelectedItem(undefined);
          setShowSidebar(false);
          setLastSelectedItem(undefined);
        } else {
          // Panel ist geschlossen -> Panel öffnen (Item bleibt selektiert)
          setShowSidebar(true);
          setSelectedItem(item);
        }
      } else {
        // Anderes Element angeklickt -> Panel öffnen und Item selektieren
        setSelectedItemIndices(new Set([itemIndex]));
        setSelectedItem(item);
        setShowSidebar(true);
        setLastSelectedItem(item);
      }
    },
    [result, selectedItems, lastSelectedItem, selectedItemIndices, showSidebar],
  );

  const closeSidebar = useCallback(() => {
    setShowSidebar(false);
  }, []);

  const value = useMemo(
    () => ({
      selectedItems,
      selectedItem,
      selectedItemIds,
      showSidebar,
      changeSelectedItems,
      isSelected,
      closeSidebar,
    }),
    [
      selectedItems,
      selectedItem,
      selectedItemIds,
      showSidebar,
      changeSelectedItems,
      isSelected,
      closeSidebar,
    ],
  );
  return (
    <ItemSelectionContext.Provider value={value}>
      {children}
    </ItemSelectionContext.Provider>
  );
};
